import { Fragment, useEffect, useMemo, useState } from 'react';
import type { RemovalSourceType } from '@shared/install-exception';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Database,
  Eye,
  ImageIcon,
  RefreshCw,
  Upload,
} from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { apiFetch } from '@/lib/api';
import { apiRequest } from '@/lib/queryClient';

type GenerationMode = 'confirmed_only' | 'confirmed_plus_planned' | 'planned_only';
type FilterMode = 'all' | 'review' | 'planned_record' | 'qty_changed' | 'repost' | 'missing_image' | 'excluded';
type GenerationDeltaFilterMode = 'all' | 'delta_only' | 'added' | 'removed' | 'qty_changed' | 'source_type_changed';

interface Retailer {
  id: number;
  name: string;
}

interface BookingCycle {
  id: number;
  name: string;
  retailerId: number;
  startDate?: string;
  endDate?: string;
  start_date?: string;
  end_date?: string;
}

interface Operator {
  id: number;
  name: string;
}

interface PreviewLine {
  previewLineId: string;
  status: string;
  sourceType: RemovalSourceType;
  isException: boolean;
  exceptionFlags: string[];
  store: { id: number | null; name: string };
  campaign: { id: number | null; name: string };
  format: { id: number | null; name: string };
  quantities: {
    plannedQty: number | null;
    confirmedQty: number | null;
    removalQty: number;
  };
  chain: {
    rootTaskId: number;
    effectiveTaskId: number;
    effectiveTaskType: string;
    selectionReason: string;
  } | null;
  sourcePosting: {
    postingEventId: number;
    reviewDecisionId: number | null;
    approvedAt: string | null;
    operatorName: string | null;
  } | null;
  image: {
    assetId: number | null;
    thumbnailUrl: string | null;
    hasImage: boolean;
    selectionReason: string | null;
  };
  fallback: {
    reason: string;
    snapshotSources: string[];
  } | null;
  reason?: { code: string; label: string };
  installException?: {
    postingEventId: number;
    disposition: 'left_with_manager' | 'removed_from_store';
    reasonCode: string;
    reasonLabel: string;
    note: string | null;
    recordedAt: string | null;
  } | null;
  audit: {
    confirmedDeliveryLineId: string | null;
    plannedSnapshotLineKey: string | null;
    wouldGenerateInModes: string[];
    packNames: string[];
  };
}

interface PreviewResponse {
  sourceCycle: { id: number; name: string };
  targetCycle: { id: number; name: string };
  generationMode: GenerationMode;
  generatedAt: string;
  summary: {
    totalCandidateLines: number;
    totalRemovalQty: number;
    confirmedLines: number;
    plannedFallbackLines: number;
    droppedZeroConfirmedLines: number;
    unresolvedLines: number;
    repostDerivedLines: number;
    missingImageWarnings: number;
    quantityChangedFromPlannedLines: number;
  };
  pipeline: {
    sentToMobile: number;
    submitted: number;
    approved: number;
  };
  readiness: {
    status: 'ready' | 'warning' | 'blocked';
    blockers: Array<{ code: string; count: number; message: string }>;
    warnings: Array<{ code: string; count: number; message: string }>;
  };
  lines: PreviewLine[];
}

interface GenerationPreviewResponse {
  sourceCycleId: number;
  targetCycleId: number;
  generationMode: GenerationMode;
  upstreamSignature: string;
  summary: PreviewResponse['summary'];
  readiness: PreviewResponse['readiness'];
  comparison: {
    existingObligationRows: number;
    generatedRows: number;
    addedRows: number;
    removedRows: number;
    quantityChangedRows: number;
    sourceTypeChangedRows: number;
  };
  obligations: Array<{
    snapshotLineId: string;
    sourceType: RemovalSourceType;
    qtyExpected: number;
    storeId: number;
    storeName: string | null;
    mediaFormatId: number;
    mediaFormatName: string | null;
    campaignId: number | null;
    campaignName: string | null;
    confirmedDeliveryLineId: string | null;
    comparison: {
      deltaType: 'added' | 'removed' | 'quantity_changed' | 'source_type_changed' | 'unchanged';
      existingQtyExpected: number | null;
      existingSourceType: RemovalSourceType | null;
    };
    provenance: {
      label: string;
      detail: string;
    };
  }>;
}

interface ShadowGenerationResponse {
  generationRunId: string;
  shadow: boolean;
  runStatus: string;
  obligationStatus: string;
  upstreamSignature: string;
  lineCount: number;
  summary: PreviewResponse['summary'];
  readiness: PreviewResponse['readiness'];
}

interface PromoteResponse {
  promotedRunId: string;
  lineCount: number;
  promotedAt: string;
}

interface SavedRun {
  id: string;
  status: string;
  generationMode: GenerationMode;
  upstreamSignature: string | null;
  lineCount: number;
  changedConfirmedLineCount: number;
  staleAt: string | null;
  generatedAt: string | null;
  obligations: Array<{
    id: string;
    sourceType: RemovalSourceType;
    qtyExpected: number;
    storeId: number;
    storeName: string | null;
    mediaFormatId: number;
    mediaFormatName: string | null;
    campaignId: number | null;
    campaignName: string | null;
    confirmedDeliveryLineId: string | null;
    sourcePostingEventId: number | null;
    sourceReviewDecisionId: number | null;
    detail: string;
  }>;
}

interface GenerationStateResponse {
  sourceCycleId: number;
  targetCycleId: number;
  generationMode: GenerationMode;
  activeRun: SavedRun | null;
  shadowRun: SavedRun | null;
}

interface PreviewGroup {
  key: string;
  storeName: string;
  campaignName: string;
  sourceType: RemovalSourceType | 'mixed';
  lineCount: number;
  removalQtyTotal: number;
  exceptionCount: number;
  lines: PreviewLine[];
}

interface SavedRunGroup {
  key: string;
  storeName: string;
  campaignName: string;
  lineCount: number;
  removalQtyTotal: number;
  sourceType: RemovalSourceType | 'mixed';
  lines: SavedRun['obligations'];
}

function summaryCards(summary: PreviewResponse['summary']) {
  return [
    { key: 'total_lines', label: 'Proposed removal lines', value: summary.totalCandidateLines },
    { key: 'total_qty', label: 'Total media to remove', value: summary.totalRemovalQty },
    { key: 'confirmed', label: 'Based on approved posting', value: summary.confirmedLines },
    { key: 'planned_record', label: 'Based on planned record', value: summary.plannedFallbackLines },
    { key: 'qty', label: 'Quantity differs from plan', value: summary.quantityChangedFromPlannedLines },
    { key: 'review', label: 'Need attention', value: summary.droppedZeroConfirmedLines + summary.unresolvedLines },
  ];
}

function getCycleStart(cycle: BookingCycle) {
  return cycle.startDate ?? cycle.start_date ?? null;
}

function getCycleEnd(cycle: BookingCycle) {
  return cycle.endDate ?? cycle.end_date ?? null;
}

function compareCyclesAscending(a: BookingCycle, b: BookingCycle) {
  const startA = getCycleStart(a);
  const startB = getCycleStart(b);

  if (startA && startB) {
    const timeA = new Date(startA).getTime();
    const timeB = new Date(startB).getTime();
    if (timeA !== timeB) {
      return timeA - timeB;
    }
  }

  return a.id - b.id;
}

function formatCycleLabel(cycle: BookingCycle) {
  const start = getCycleStart(cycle);
  const end = getCycleEnd(cycle);
  if (!start || !end) return cycle.name;

  return `${cycle.name} (${new Date(start).toLocaleDateString()} to ${new Date(end).toLocaleDateString()})`;
}

function formatReadinessMessage(item: { code: string; count: number; message: string }): string {
  switch (item.code) {
    case 'PLANNED_FALLBACK_PRESENT':
      return `${item.count.toLocaleString()} removal lines have no approved posting and are using the planned record`;
    case 'MISSING_CONFIRMED_SOURCE':
      return `${item.count.toLocaleString()} removal lines have no approved posting source`;
    case 'ZERO_CONFIRMED_DROPPED':
      return `${item.count.toLocaleString()} approved posting lines had zero quantity and were excluded`;
    case 'MISSING_IMAGE':
      return `${item.count.toLocaleString()} approved posting lines are missing photos`;
    default:
      return item.message;
  }
}

function formatExceptionFlag(flag: string): string {
  switch (flag) {
    case 'PLANNED_FALLBACK':
      return 'Planned record';
    case 'QTY_DIFFERS_FROM_PLANNED':
      return 'Quantity differs';
    case 'REPOST_DERIVED':
      return 'Repost';
    case 'MISSING_IMAGE':
      return 'No photo';
    default:
      return flag.replace(/_/g, ' ').toLowerCase();
  }
}

function formatComparisonStatus(row: GenerationPreviewResponse['obligations'][number]) {
  switch (row.comparison.deltaType) {
    case 'added':
      return 'Will be added to live removals';
    case 'removed':
      return 'Will be removed from live removals';
    case 'quantity_changed':
      return `Live quantity will change from ${row.comparison.existingQtyExpected ?? '—'} to ${row.qtyExpected}`;
    case 'source_type_changed':
      return `Same removal, now based on ${sourceLabel(row.sourceType).toLowerCase()}`;
    default:
      return 'Already matches the live removal set';
  }
}

function formatCurrentLiveState(row: GenerationPreviewResponse['obligations'][number]) {
  if (!row.comparison.existingSourceType) {
    return 'This line is not currently in the live removal set';
  }

  const evidence = sourceLabel(row.comparison.existingSourceType).toLowerCase();

  return `${row.comparison.existingQtyExpected ?? '—'} units, based on ${evidence}`;
}

function formatWhyIncluded(row: GenerationPreviewResponse['obligations'][number]) {
  if (row.sourceType === 'confirmed_delivery' || row.sourceType === 'left_with_manager') {
    return row.provenance.detail;
  }

  return row.provenance.detail
    ? `No approved posting found. Using planned record: ${row.provenance.detail}`
    : 'No approved posting found. Using planned record from the earlier cycle.';
}

function groupPreviewLines(lines: PreviewLine[]): PreviewGroup[] {
  const groups = new Map<string, PreviewGroup>();

  for (const line of lines) {
    const key = `${line.store.id ?? 'store'}:${line.campaign.id ?? 'campaign'}`;
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, {
        key,
        storeName: line.store.name,
        campaignName: line.campaign.name,
        sourceType: line.sourceType,
        lineCount: 1,
        removalQtyTotal: line.quantities.removalQty,
        exceptionCount: line.isException ? 1 : 0,
        lines: [line],
      });
      continue;
    }
    existing.lines.push(line);
    existing.lineCount += 1;
    existing.removalQtyTotal += line.quantities.removalQty;
    existing.exceptionCount += line.isException ? 1 : 0;
    if (existing.sourceType !== line.sourceType) {
      existing.sourceType = 'mixed';
    }
  }

  return Array.from(groups.values()).sort((a, b) => {
    if (a.storeName !== b.storeName) return a.storeName.localeCompare(b.storeName);
    return a.campaignName.localeCompare(b.campaignName);
  });
}

function groupSavedRunLines(lines: SavedRun['obligations']): SavedRunGroup[] {
  const groups = new Map<string, SavedRunGroup>();

  for (const line of lines) {
    const key = `${line.storeId}:${line.campaignId ?? 'campaign'}`;
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, {
        key,
        storeName: line.storeName ?? `Store ${line.storeId}`,
        campaignName: line.campaignName ?? `Campaign ${line.campaignId ?? '—'}`,
        lineCount: 1,
        removalQtyTotal: line.qtyExpected,
        sourceType: line.sourceType,
        lines: [line],
      });
      continue;
    }
    existing.lines.push(line);
    existing.lineCount += 1;
    existing.removalQtyTotal += line.qtyExpected;
    if (existing.sourceType !== line.sourceType) {
      existing.sourceType = 'mixed';
    }
  }

  return Array.from(groups.values()).sort((a, b) => {
    if (a.storeName !== b.storeName) return a.storeName.localeCompare(b.storeName);
    return a.campaignName.localeCompare(b.campaignName);
  });
}

function sourceLabel(sourceType: RemovalSourceType) {
  switch (sourceType) {
    case 'confirmed_delivery':
      return 'Approved posting';
    case 'planned_delivery':
      return 'Planned record';
    case 'left_with_manager':
      return 'Possible removal (left with manager)';
    default: {
      const unhandled: never = sourceType;
      return String(unhandled);
    }
  }
}

function sourceBadgeText(sourceType: RemovalSourceType | 'mixed') {
  if (sourceType === 'mixed') return 'Mixed';
  return sourceLabel(sourceType);
}

function runSummary(run: SavedRun | null) {
  if (!run) {
    return {
      totalQty: 0,
      confirmedLines: 0,
      plannedLines: 0,
    };
  }

  return run.obligations.reduce((acc, line) => {
    acc.totalQty += line.qtyExpected;
    if (line.sourceType === 'confirmed_delivery') acc.confirmedLines += 1;
    if (line.sourceType === 'planned_delivery') acc.plannedLines += 1;
    return acc;
  }, {
    totalQty: 0,
    confirmedLines: 0,
    plannedLines: 0,
  });
}

function stateLabel(run: SavedRun) {
  if (run.status === 'ACTIVE') return 'Live';
  if (run.status === 'SHADOW') return 'Shadow';
  return run.status;
}

function SavedRunSection({ title, description, run }: { title: string; description: string; run: SavedRun | null }) {
  const groups = useMemo(() => groupSavedRunLines(run?.obligations ?? []), [run]);
  const summary = useMemo(() => runSummary(run), [run]);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const toggle = (key: string) => {
    setExpandedKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
        <div className="text-sm text-muted-foreground">{description}</div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!run && (
          <div className="rounded-lg border py-8 text-center text-sm text-muted-foreground">
            No saved run yet.
          </div>
        )}

        {run && (
          <>
            <div className="grid gap-4 md:grid-cols-4">
              <div>
                <div className="text-sm font-medium text-muted-foreground">Status</div>
                <div className="mt-1 flex items-center gap-2">
                  <Badge variant={run.status === 'ACTIVE' ? 'default' : 'outline'}>{stateLabel(run)}</Badge>
                  {run.staleAt && <Badge variant="destructive">Stale</Badge>}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">Saved removal lines</div>
                <div className="mt-1 text-2xl font-semibold">{run.obligations.length.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">Total media to remove</div>
                <div className="mt-1 text-2xl font-semibold">{summary.totalQty.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">Generated</div>
                <div className="mt-1 text-sm">{run.generatedAt ? new Date(run.generatedAt).toLocaleString() : '—'}</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Badge variant="default">{summary.confirmedLines.toLocaleString()} backed by approved posting</Badge>
              <Badge variant="outline">{summary.plannedLines.toLocaleString()} based on planned record</Badge>
            </div>

            {run.staleAt && (
              <Alert variant="default">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Saved removals are stale</AlertTitle>
                <AlertDescription>
                  Approved posting data changed after this run was written. Regenerate removals before relying on this set.
                  {run.changedConfirmedLineCount > 0 ? ` ${run.changedConfirmedLineCount.toLocaleString()} confirmed posting lines changed.` : ''}
                </AlertDescription>
              </Alert>
            )}

            {groups.map((group) => (
              <Card key={group.key} className="border border-border/70 shadow-none">
                <CardContent className="p-0">
                  <div className="flex items-start justify-between gap-4 border-b px-5 py-4">
                    <div className="space-y-1">
                      <div className="text-xl font-semibold">{group.storeName}</div>
                      <div className="text-base text-muted-foreground">{group.campaignName}</div>
                      <div className="flex flex-wrap gap-2 pt-1 text-sm text-muted-foreground">
                        <span>{group.lineCount} format lines</span>
                        <span>{group.removalQtyTotal} total removal qty</span>
                      </div>
                    </div>
                    <Badge variant={group.sourceType === 'planned_delivery' ? 'outline' : 'default'}>
                      {sourceBadgeText(group.sourceType)}
                    </Badge>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[44px]" />
                        <TableHead>Media format</TableHead>
                        <TableHead>Removal quantity</TableHead>
                        <TableHead>Why this line exists</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.lines.map((line) => {
                        const expanded = expandedKeys.has(line.id);
                        return (
                          <Fragment key={line.id}>
                            <TableRow>
                              <TableCell>
                                <Button variant="ghost" size="icon" onClick={() => toggle(line.id)}>
                                  {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </Button>
                              </TableCell>
                              <TableCell className="font-medium">{line.mediaFormatName ?? `Format ${line.mediaFormatId}`}</TableCell>
                              <TableCell>
                                <div className="font-medium">{line.qtyExpected} units</div>
                                <div className="text-xs text-muted-foreground">{sourceLabel(line.sourceType)}</div>
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">{line.detail}</TableCell>
                            </TableRow>
                            {expanded && (
                              <TableRow>
                                <TableCell colSpan={4} className="bg-muted/30 text-sm">
                                  <div className="grid gap-2 md:grid-cols-2">
                                    <div><span className="font-medium">Posting event:</span> {line.sourcePostingEventId ?? '—'}</div>
                                    <div><span className="font-medium">Review decision:</span> {line.sourceReviewDecisionId ?? '—'}</div>
                                    <div><span className="font-medium">Confirmed delivery line:</span> {line.confirmedDeliveryLineId ?? '—'}</div>
                                    <div><span className="font-medium">Saved line:</span> {line.id}</div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminRemovalsPreviewPage() {
  const [selectedRetailerId, setSelectedRetailerId] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>(() => new Date().getFullYear().toString());
  const [sourceCycleId, setSourceCycleId] = useState<number | null>(null);
  const [targetCycleId, setTargetCycleId] = useState<number | null>(null);
  const [generationMode, setGenerationMode] = useState<GenerationMode>('confirmed_plus_planned');
  const [onlyExceptions, setOnlyExceptions] = useState(false);
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [generationDeltaFilterMode, setGenerationDeltaFilterMode] = useState<GenerationDeltaFilterMode>('delta_only');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectedOperator, setSelectedOperator] = useState<string>('all');
  const [selectedStore, setSelectedStore] = useState<string>('all');

  const yearOptions = useMemo(() => {
    const baseYear = new Date().getFullYear();
    return [baseYear - 1, baseYear, baseYear + 1];
  }, []);

  const { data: retailers = [] } = useQuery<Retailer[]>({
    queryKey: ['/api/retailers'],
    queryFn: async () => {
      const res = await apiFetch('/api/retailers');
      if (!res.ok) throw new Error('Failed to fetch retailers');
      const data = await res.json();
      return Array.isArray(data) ? data : (data.data || []);
    },
  });

  const { data: cycles = [] } = useQuery<BookingCycle[]>({
    queryKey: ['/api/booking-cycles', selectedRetailerId, selectedYear],
    enabled: !!selectedRetailerId,
    queryFn: async () => {
      const yearQuery = selectedYear ? `&year=${selectedYear}` : '';
      const res = await apiFetch(`/api/booking-cycles?retailerId=${selectedRetailerId}${yearQuery}`);
      if (!res.ok) throw new Error('Failed to fetch cycles');
      const data = await res.json();
      const rows = Array.isArray(data) ? data : (data.data || []);
      return rows
        .filter((cycle: any) => cycle.retailerId === selectedRetailerId || cycle.retailer_id === selectedRetailerId)
        .sort((a: BookingCycle, b: BookingCycle) => compareCyclesAscending(a, b));
    },
  });

  const filteredCycles = useMemo(() => {
    if (!selectedYear) return cycles;
    const selectedYearNumber = Number(selectedYear);
    return cycles.filter((cycle) => {
      const startYear = getCycleStart(cycle) ? new Date(getCycleStart(cycle) as string).getFullYear() : null;
      const endYear = getCycleEnd(cycle) ? new Date(getCycleEnd(cycle) as string).getFullYear() : null;
      const cycleYear = startYear ?? endYear;
      return cycleYear ? cycleYear === selectedYearNumber : true;
    });
  }, [cycles, selectedYear]);

  const { data: operators = [] } = useQuery<Operator[]>({
    queryKey: ['/api/users/operators'],
    queryFn: async () => {
      const res = await apiFetch('/api/users/operators');
      if (!res.ok) throw new Error('Failed to fetch operators');
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
  });

  const sourceCycle = useMemo(
    () => cycles.find((cycle) => cycle.id === sourceCycleId) ?? null,
    [cycles, sourceCycleId],
  );

  const inferredTargetCycle = useMemo(() => {
    if (!sourceCycle) return null;
    const sourceIndex = cycles.findIndex((cycle) => cycle.id === sourceCycle.id);
    return sourceIndex === -1 ? null : (cycles[sourceIndex + 1] ?? null);
  }, [cycles, sourceCycle]);

  useEffect(() => {
    const nextTargetId = inferredTargetCycle?.id ?? null;
    setTargetCycleId((current) => (current === nextTargetId ? current : nextTargetId));
  }, [inferredTargetCycle]);

  const previewMutation = useMutation<PreviewResponse, Error>({
    mutationFn: async () => {
      if (!sourceCycleId || !targetCycleId) throw new Error('Select source and target cycles');
      const response = await apiRequest('/api/removals/preview', 'POST', {
        sourceCycleId,
        targetCycleId,
        generationMode,
        filters: { onlyExceptions },
      });
      return response.json();
    },
  });

  const generationPreviewMutation = useMutation<GenerationPreviewResponse, Error>({
    mutationFn: async () => {
      if (!sourceCycleId || !targetCycleId) throw new Error('Select source and target cycles');
      const response = await apiRequest('/api/removals/generation-preview', 'POST', {
        sourceCycleId,
        targetCycleId,
        generationMode,
      });
      return response.json();
    },
  });

  const generationStateMutation = useMutation<GenerationStateResponse, Error>({
    mutationFn: async () => {
      if (!sourceCycleId || !targetCycleId) throw new Error('Select source and target cycles');
      const response = await apiRequest('/api/removals/generation-state', 'POST', {
        sourceCycleId,
        targetCycleId,
        generationMode,
      });
      return response.json();
    },
  });

  const shadowGenerationMutation = useMutation<ShadowGenerationResponse, Error>({
    mutationFn: async () => {
      if (!sourceCycleId || !targetCycleId) throw new Error('Select source and target cycles');
      const response = await apiRequest('/api/removals/generation-shadow', 'POST', {
        sourceCycleId,
        targetCycleId,
        generationMode,
      });
      return response.json();
    },
    onSuccess: () => {
      generationPreviewMutation.mutate();
      generationStateMutation.mutate();
    },
  });

  const promoteMutation = useMutation<PromoteResponse, Error>({
    mutationFn: async () => {
      if (!sourceCycleId || !targetCycleId) throw new Error('Select source and target cycles');
      const response = await apiRequest('/api/removals/generation-promote', 'POST', {
        sourceCycleId,
        targetCycleId,
        generationMode,
      });
      return response.json();
    },
    onSuccess: () => {
      generationPreviewMutation.mutate();
      generationStateMutation.mutate();
    },
  });

  const buildAllViews = () => {
    previewMutation.mutate();
    generationPreviewMutation.mutate();
    generationStateMutation.mutate();
  };

  const storeOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const line of previewMutation.data?.lines ?? []) {
      if (line.store.id != null) map.set(line.store.id, line.store.name);
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [previewMutation.data?.lines]);

  const filteredLines = useMemo(() => {
    let lines = previewMutation.data?.lines ?? [];

    if (selectedOperator !== 'all') {
      const selectedOperatorName = operators.find((operator) => String(operator.id) === selectedOperator)?.name;
      lines = lines.filter((line) => (line.sourcePosting?.operatorName ?? '') === (selectedOperatorName ?? ''));
    }

    if (selectedStore !== 'all') {
      lines = lines.filter((line) => String(line.store.id ?? '') === selectedStore);
    }

    switch (filterMode) {
      case 'review':
        return lines.filter((line) => line.isException);
      case 'planned_record':
        return lines.filter((line) => line.exceptionFlags.includes('PLANNED_FALLBACK'));
      case 'qty_changed':
        return lines.filter((line) => line.exceptionFlags.includes('QTY_DIFFERS_FROM_PLANNED'));
      case 'repost':
        return lines.filter((line) => line.exceptionFlags.includes('REPOST_DERIVED'));
      case 'missing_image':
        return lines.filter((line) => line.exceptionFlags.includes('MISSING_IMAGE'));
      case 'excluded':
        return lines.filter((line) => line.status !== 'candidate');
      default:
        return lines;
    }
  }, [filterMode, operators, previewMutation.data?.lines, selectedOperator, selectedStore]);

  const groupedLines = useMemo(() => groupPreviewLines(filteredLines), [filteredLines]);

  const filteredGeneratedObligations = useMemo(() => {
    const rows = generationPreviewMutation.data?.obligations ?? [];
    switch (generationDeltaFilterMode) {
      case 'delta_only':
        return rows.filter((row) => row.comparison.deltaType !== 'unchanged');
      case 'added':
        return rows.filter((row) => row.comparison.deltaType === 'added');
      case 'removed':
        return rows.filter((row) => row.comparison.deltaType === 'removed');
      case 'qty_changed':
        return rows.filter((row) => row.comparison.deltaType === 'quantity_changed');
      case 'source_type_changed':
        return rows.filter((row) => row.comparison.deltaType === 'source_type_changed');
      default:
        return rows;
    }
  }, [generationDeltaFilterMode, generationPreviewMutation.data?.obligations]);

  const groupedGeneratedRows = useMemo(() => {
    const groups = new Map<string, {
      key: string;
      storeName: string;
      campaignName: string;
      lineCount: number;
      removalQtyTotal: number;
      sourceType: RemovalSourceType | 'mixed';
      rows: GenerationPreviewResponse['obligations'];
    }>();

    for (const row of filteredGeneratedObligations) {
      const key = `${row.storeId}:${row.campaignId ?? 'campaign'}`;
      const existing = groups.get(key);
      if (!existing) {
        groups.set(key, {
          key,
          storeName: row.storeName ?? `Store ${row.storeId}`,
          campaignName: row.campaignName ?? `Campaign ${row.campaignId ?? '—'}`,
          lineCount: 1,
          removalQtyTotal: row.qtyExpected,
          sourceType: row.sourceType,
          rows: [row],
        });
        continue;
      }
      existing.rows.push(row);
      existing.lineCount += 1;
      existing.removalQtyTotal += row.qtyExpected;
      if (existing.sourceType !== row.sourceType) existing.sourceType = 'mixed';
    }

    return Array.from(groups.values()).sort((a, b) => {
      if (a.storeName !== b.storeName) return a.storeName.localeCompare(b.storeName);
      return a.campaignName.localeCompare(b.campaignName);
    });
  }, [filteredGeneratedObligations]);

  const toggleExpanded = (previewLineId: string) => {
    setExpandedRows((current) => {
      const next = new Set(current);
      if (next.has(previewLineId)) next.delete(previewLineId);
      else next.add(previewLineId);
      return next;
    });
  };

  const shadowRun = generationStateMutation.data?.shadowRun ?? null;
  const activeRun = generationStateMutation.data?.activeRun ?? null;
  const sourceCycleDisplay = sourceCycle ? formatCycleLabel(sourceCycle) : previewMutation.data?.sourceCycle.name;
  const targetCycleDisplay = inferredTargetCycle ? formatCycleLabel(inferredTargetCycle) : previewMutation.data?.targetCycle.name;

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Removals"
        description="Load the proposed removal set, write a saved shadow set, compare it with the current live set, and promote it only when you are satisfied."
        icon={Eye}
      >
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={buildAllViews}
            disabled={!sourceCycleId || !targetCycleId || previewMutation.isPending || generationPreviewMutation.isPending || generationStateMutation.isPending}
          >
            {(previewMutation.isPending || generationPreviewMutation.isPending || generationStateMutation.isPending)
              ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              : <Eye className="mr-2 h-4 w-4" />}
            Load removals
          </Button>
          <Button
            variant="secondary"
            onClick={() => shadowGenerationMutation.mutate()}
            disabled={!sourceCycleId || !targetCycleId || shadowGenerationMutation.isPending}
          >
            {shadowGenerationMutation.isPending
              ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              : <Database className="mr-2 h-4 w-4" />}
            Generate shadow removals
          </Button>
          <Button
            onClick={() => promoteMutation.mutate()}
            disabled={!shadowRun || !!shadowRun.staleAt || promoteMutation.isPending}
          >
            {promoteMutation.isPending
              ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              : <Upload className="mr-2 h-4 w-4" />}
            Promote shadow to live
          </Button>
        </div>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-5">
          <div className="space-y-2">
            <div className="text-sm font-medium">Retailer</div>
            <Select
              value={selectedRetailerId ? String(selectedRetailerId) : undefined}
              onValueChange={(value) => {
                const nextRetailerId = Number(value);
                setSelectedRetailerId(nextRetailerId);
                setSelectedYear(new Date().getFullYear().toString());
                setSourceCycleId(null);
                setTargetCycleId(null);
                setSelectedOperator('all');
                setSelectedStore('all');
                previewMutation.reset();
                generationPreviewMutation.reset();
                generationStateMutation.reset();
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select retailer" />
              </SelectTrigger>
              <SelectContent>
                {retailers.map((retailer) => (
                  <SelectItem key={retailer.id} value={String(retailer.id)}>{retailer.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">Year</div>
            <Select
              value={selectedYear}
              onValueChange={(value) => {
                setSelectedYear(value);
                setSourceCycleId(null);
                setTargetCycleId(null);
                previewMutation.reset();
                generationPreviewMutation.reset();
                generationStateMutation.reset();
              }}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {yearOptions.map((year) => (
                  <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">Posting cycle</div>
            <Select
              value={sourceCycleId ? String(sourceCycleId) : undefined}
              onValueChange={(value) => {
                setSourceCycleId(Number(value));
                setSelectedStore('all');
                previewMutation.reset();
                generationPreviewMutation.reset();
                generationStateMutation.reset();
              }}
              disabled={!selectedRetailerId}
            >
              <SelectTrigger><SelectValue placeholder="Select source cycle" /></SelectTrigger>
              <SelectContent>
                {filteredCycles.map((cycle) => (
                  <SelectItem key={cycle.id} value={String(cycle.id)}>
                    {`Cycle ${cycle.name} (ID ${cycle.id})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">Operator</div>
            <Select value={selectedOperator} onValueChange={setSelectedOperator}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All operators</SelectItem>
                {operators.map((operator) => (
                  <SelectItem key={operator.id} value={String(operator.id)}>{operator.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">Store</div>
            <Select value={selectedStore} onValueChange={setSelectedStore}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All stores</SelectItem>
                {storeOptions.map((store) => (
                  <SelectItem key={store.id} value={String(store.id)}>{store.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2 md:col-span-5">
            <div className="text-sm font-medium">Removal basis</div>
            <Select
              value={generationMode}
              onValueChange={(value) => {
                setGenerationMode(value as GenerationMode);
                previewMutation.reset();
                generationPreviewMutation.reset();
                generationStateMutation.reset();
              }}
            >
              <SelectTrigger className="max-w-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="confirmed_plus_planned">Approved posting + planned record (recommended)</SelectItem>
                <SelectItem value="confirmed_only">Approved posting only</SelectItem>
                <SelectItem value="planned_only">Planned record only</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Approved posting means the previous posting was submitted and approved. Planned record means no approved posting was available, so the removal is based on the planned or paper assignment from the earlier cycle.
            </p>
          </div>

          <div className="flex items-center space-x-2 md:col-span-5">
            <Checkbox
              id="only-exceptions"
              checked={onlyExceptions}
              onCheckedChange={(checked) => {
                setOnlyExceptions(Boolean(checked));
                previewMutation.reset();
              }}
            />
            <label htmlFor="only-exceptions" className="text-sm">Only show items needing attention</label>
          </div>
        </CardContent>
      </Card>

      {previewMutation.error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Removals load failed</AlertTitle>
          <AlertDescription>{previewMutation.error.message}</AlertDescription>
        </Alert>
      )}

      {generationPreviewMutation.error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Saved comparison unavailable</AlertTitle>
          <AlertDescription>{generationPreviewMutation.error.message}</AlertDescription>
        </Alert>
      )}

      {generationStateMutation.error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Saved removal runs unavailable</AlertTitle>
          <AlertDescription>{generationStateMutation.error.message}</AlertDescription>
        </Alert>
      )}

      {shadowGenerationMutation.data && (
        <Alert>
          <Database className="h-4 w-4" />
          <AlertTitle>Shadow removals saved</AlertTitle>
          <AlertDescription>
            Saved {shadowGenerationMutation.data.lineCount.toLocaleString()} shadow removal lines in run {shadowGenerationMutation.data.generationRunId}. They are stored separately from the live removal set until you promote them.
          </AlertDescription>
        </Alert>
      )}

      {promoteMutation.data && (
        <Alert>
          <Upload className="h-4 w-4" />
          <AlertTitle>Shadow removals promoted</AlertTitle>
          <AlertDescription>
            Promoted {promoteMutation.data.lineCount.toLocaleString()} removal lines into the live set at {new Date(promoteMutation.data.promotedAt).toLocaleString()}.
          </AlertDescription>
        </Alert>
      )}

      {previewMutation.data && (
        <>
          <Card>
            <CardContent className="grid gap-4 pt-6 md:grid-cols-2">
              <div>
                <div className="text-sm font-medium text-muted-foreground">Previous posting cycle</div>
                <div className="mt-1 text-base font-medium">{sourceCycleDisplay}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">Next removal cycle</div>
                <div className="mt-1 text-base font-medium">{targetCycleDisplay}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Removal Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-lg border p-4">
                  <div className="text-sm font-medium text-muted-foreground">Step 1</div>
                  <div className="mt-1 font-medium">Load the proposed removal set</div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    Review the removal lines below for {sourceCycleDisplay} into {targetCycleDisplay}.
                  </div>
                </div>
                <div className="rounded-lg border p-4">
                  <div className="text-sm font-medium text-muted-foreground">Step 2</div>
                  <div className="mt-1 font-medium">Save a shadow set</div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    This writes a frozen removal set for review without changing the current live removals.
                  </div>
                </div>
                <div className="rounded-lg border p-4">
                  <div className="text-sm font-medium text-muted-foreground">Step 3</div>
                  <div className="mt-1 font-medium">Promote shadow to live</div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    Only do this after you are satisfied with the saved shadow set and it is not stale.
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
            {summaryCards(previewMutation.data.summary).map((card) => (
              <Card key={card.key}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">{card.label}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-semibold">{card.value}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {(previewMutation.data.readiness.blockers.length > 0 || previewMutation.data.readiness.warnings.length > 0) && (
            <Alert variant={previewMutation.data.readiness.blockers.length > 0 ? 'destructive' : 'default'}>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>
                {previewMutation.data.readiness.blockers.length > 0 ? 'Removals blocked' : 'Removals warnings'}
              </AlertTitle>
              <AlertDescription className="space-y-1">
                {previewMutation.data.readiness.blockers.map((item) => (
                  <div key={`blocker-${item.code}`}>{formatReadinessMessage(item)}</div>
                ))}
                {previewMutation.data.readiness.warnings.map((item) => (
                  <div key={`warning-${item.code}`}>{formatReadinessMessage(item)}</div>
                ))}
              </AlertDescription>
            </Alert>
          )}

          {(activeRun?.staleAt || shadowRun?.staleAt) && (
            <Alert variant="default">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Saved removal sets need regeneration</AlertTitle>
              <AlertDescription>
                {activeRun?.staleAt && 'The live removal set is stale because approved posting data changed. '}
                {shadowRun?.staleAt && 'The saved shadow set is stale and should be regenerated before promotion. '}
                Use Generate shadow removals to rebuild from the latest approved posting truth.
              </AlertDescription>
            </Alert>
          )}

          {generationPreviewMutation.data && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">What Would Change If You Promote These Removals?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-4">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Current live removal lines</div>
                    <div className="mt-1 text-2xl font-semibold">{generationPreviewMutation.data.comparison.existingObligationRows.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Proposed removal lines</div>
                    <div className="mt-1 text-2xl font-semibold">{generationPreviewMutation.data.comparison.generatedRows.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">New removal lines</div>
                    <div className="mt-1 text-2xl font-semibold">{generationPreviewMutation.data.comparison.addedRows.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Removal lines no longer needed</div>
                    <div className="mt-1 text-2xl font-semibold">{generationPreviewMutation.data.comparison.removedRows.toLocaleString()}</div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="text-sm text-muted-foreground">
                    {generationPreviewMutation.data.comparison.quantityChangedRows.toLocaleString()} lines would change quantity.{' '}
                    {generationPreviewMutation.data.comparison.sourceTypeChangedRows.toLocaleString()} lines would keep the same quantity but change from planned record to approved posting, or vice versa.
                  </div>
                  <div className="flex justify-start md:justify-end">
                    <Select value={generationDeltaFilterMode} onValueChange={(value) => setGenerationDeltaFilterMode(value as GenerationDeltaFilterMode)}>
                      <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="delta_only">Only lines that would change</SelectItem>
                        <SelectItem value="all">All proposed removal lines</SelectItem>
                        <SelectItem value="added">New removal lines</SelectItem>
                        <SelectItem value="removed">No longer needed</SelectItem>
                        <SelectItem value="qty_changed">Quantity updated</SelectItem>
                        <SelectItem value="source_type_changed">Support changed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {groupedGeneratedRows.map((group) => (
                  <Card key={group.key} className="border border-border/70 shadow-none">
                    <CardContent className="p-0">
                      <div className="flex items-start justify-between gap-4 border-b px-5 py-4">
                        <div className="space-y-1">
                          <div className="text-xl font-semibold">{group.storeName}</div>
                          <div className="text-base text-muted-foreground">{group.campaignName}</div>
                          <div className="flex flex-wrap gap-2 pt-1 text-sm text-muted-foreground">
                            <span>{group.lineCount} format lines</span>
                            <span>{group.removalQtyTotal} total removal qty</span>
                          </div>
                        </div>
                        <Badge variant={group.sourceType === 'planned_delivery' ? 'outline' : 'default'}>
                          {sourceBadgeText(group.sourceType)}
                        </Badge>
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Media format</TableHead>
                            <TableHead>Removal quantity</TableHead>
                            <TableHead>If promoted</TableHead>
                            <TableHead>Current live set</TableHead>
                            <TableHead>Why this is included</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.rows.map((row) => (
                            <TableRow key={row.snapshotLineId}>
                              <TableCell className="font-medium">{row.mediaFormatName ?? `Format ${row.mediaFormatId}`}</TableCell>
                              <TableCell>
                                <div>{row.qtyExpected} units</div>
                                <div className="text-xs text-muted-foreground">{sourceLabel(row.sourceType)}</div>
                              </TableCell>
                              <TableCell className="text-sm">{formatComparisonStatus(row)}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">{formatCurrentLiveState(row)}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">{formatWhyIncluded(row)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                ))}

                {groupedGeneratedRows.length === 0 && (
                  <div className="rounded-lg border py-10 text-center text-muted-foreground">
                    No proposed removal lines matched this filter.
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <div className="grid gap-6 lg:grid-cols-2">
            <SavedRunSection
              title="Saved shadow removals"
              description="This is the latest frozen shadow set. It is safe to inspect and compare without affecting the live removal set."
              run={shadowRun}
            />
            <SavedRunSection
              title="Current live removals"
              description="This is the removal set currently marked live. Operators and downstream flows should only rely on this set."
              run={activeRun}
            />
          </div>

          <Card>
            <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <CardTitle>Proposed Removal Lines</CardTitle>
              <div className="flex flex-wrap gap-2">
                <Select value={filterMode} onValueChange={(value) => setFilterMode(value as FilterMode)}>
                  <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All removal lines</SelectItem>
                    <SelectItem value="review">Need attention</SelectItem>
                    <SelectItem value="planned_record">Planned record</SelectItem>
                    <SelectItem value="qty_changed">Quantity differs</SelectItem>
                    <SelectItem value="repost">From reposts</SelectItem>
                    <SelectItem value="missing_image">Missing photo</SelectItem>
                    <SelectItem value="excluded">Excluded</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {groupedLines.map((group) => (
                <Card key={group.key} className="border border-border/70 shadow-none">
                  <CardContent className="p-0">
                    <div className="flex items-start justify-between gap-4 border-b px-5 py-4">
                      <div className="space-y-1">
                        <div className="text-xl font-semibold">{group.storeName}</div>
                        <div className="text-base text-muted-foreground">{group.campaignName}</div>
                        <div className="flex flex-wrap gap-2 pt-1 text-sm text-muted-foreground">
                          <span>{group.lineCount} format lines</span>
                          <span>{group.removalQtyTotal} total removal qty</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={group.sourceType === 'planned_delivery' ? 'outline' : 'default'}>
                          {sourceBadgeText(group.sourceType)}
                        </Badge>
                        {group.exceptionCount > 0 && <Badge variant="outline">{group.exceptionCount} need attention</Badge>}
                      </div>
                    </div>

                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[44px]" />
                          <TableHead>Media format</TableHead>
                          <TableHead>Removal quantity</TableHead>
                          <TableHead>Why this line exists</TableHead>
                          <TableHead>Photo</TableHead>
                          <TableHead>Attention needed</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.lines.map((line) => {
                          const expanded = expandedRows.has(line.previewLineId);
                          return (
                            <Fragment key={line.previewLineId}>
                              <TableRow>
                                <TableCell>
                                  <Button variant="ghost" size="icon" onClick={() => toggleExpanded(line.previewLineId)}>
                                    {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                  </Button>
                                </TableCell>
                                <TableCell className="font-medium">{line.format.name}</TableCell>
                                <TableCell>
                                  <div className="font-medium">{line.quantities.removalQty} units</div>
                                  <div className="text-xs text-muted-foreground">
                                    planned: {line.quantities.plannedQty ?? '-'} · approved: {line.quantities.confirmedQty ?? '-'}
                                  </div>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {line.sourceType === 'left_with_manager'
                                    ? line.reason?.label ?? sourceLabel(line.sourceType)
                                    : line.sourceType === 'confirmed_delivery'
                                    ? `Approved posting${line.sourcePosting?.postingEventId ? ` #${line.sourcePosting.postingEventId}` : ''}${line.sourcePosting?.approvedAt ? ` approved ${new Date(line.sourcePosting.approvedAt).toLocaleDateString()}` : ''}`
                                    : `No approved posting found. ${line.fallback?.reason ?? 'Using planned record from the earlier cycle.'}`}
                                </TableCell>
                                <TableCell>
                                  {line.image.thumbnailUrl ? (
                                    <img
                                      src={line.image.thumbnailUrl}
                                      alt={`${line.campaign.name} evidence`}
                                      className="h-14 w-14 rounded object-cover border"
                                    />
                                  ) : (
                                    <div
                                      className="flex h-14 w-14 items-center justify-center rounded border bg-muted text-muted-foreground"
                                      title={line.image.assetId ? 'Image temporarily unavailable' : 'No source image'}
                                    >
                                      <ImageIcon className="h-4 w-4" />
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-wrap gap-1">
                                    {line.status !== 'candidate' && (
                                      <Badge variant="destructive">{line.status === 'dropped' ? 'Excluded' : line.status}</Badge>
                                    )}
                                    {line.exceptionFlags.map((flag) => (
                                      <Badge key={flag} variant="outline">{formatExceptionFlag(flag)}</Badge>
                                    ))}
                                  </div>
                                </TableCell>
                              </TableRow>
                              {expanded && (
                                <TableRow>
                                  <TableCell colSpan={6} className="bg-muted/30">
                                    <div className="grid gap-4 md:grid-cols-2 text-sm">
                                      <div className="space-y-1">
                                        <div><span className="font-medium">Root task:</span> {line.chain?.rootTaskId ?? '—'}</div>
                                        <div><span className="font-medium">Effective task:</span> {line.chain?.effectiveTaskId ?? '—'}</div>
                                        <div><span className="font-medium">Task type:</span> {line.chain?.effectiveTaskType ?? '—'}</div>
                                        <div><span className="font-medium">Operator on posting:</span> {line.sourcePosting?.operatorName ?? '—'}</div>
                                      </div>
                                      <div className="space-y-1">
                                        <div><span className="font-medium">Selection detail:</span> {line.chain?.selectionReason ?? line.fallback?.reason ?? '—'}</div>
                                        <div><span className="font-medium">Planned record key:</span> {line.audit.plannedSnapshotLineKey ?? '—'}</div>
                                        <div><span className="font-medium">Confirmed delivery ref:</span> {line.audit.confirmedDeliveryLineId ?? '—'}</div>
                                        <div><span className="font-medium">Pack names:</span> {line.audit.packNames.join(', ') || '—'}</div>
                                      </div>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))}

              {groupedLines.length === 0 && (
                <div className="rounded-lg border py-10 text-center text-muted-foreground">
                  {onlyExceptions ? 'No removal lines needing attention were found for these filters.' : 'No removal lines were found for these filters.'}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
